/** Minimal UI translation for the React shell.
 *
 *  This translates *chrome* only — buttons, labels, headings, helper text. The
 *  conversation itself is translated server-side from the per-language config
 *  files (`llr_ire_v3.pa.json` etc.), and that remains the single source of
 *  truth for anything the engine says. Nothing here can affect routing.
 *
 *  Deliberately not react-i18next: we need lookup and a fallback, not plurals,
 *  interpolation or lazy namespaces, and the frontend has stayed dependency-thin.
 *
 *  THE RULE THAT MATTERS: translate labels, never values. Dropdown options carry
 *  an explicit English `value` that is transmitted to Joy and matched against a
 *  closed vocabulary; `t()` supplies only what the resident reads. See
 *  ReferralForm.tsx and the guard in ReferralFormI18n.test.tsx.
 */
import { createContext, useCallback, useContext, useMemo } from "react";
import type { ReactNode } from "react";

import en from "./strings.en.json";
import pa from "./strings.pa.json";
import gu from "./strings.gu.json";
import ur from "./strings.ur.json";

type Strings = Record<string, string>;

const CATALOGUES: Record<string, Strings> = { en, pa, gu, ur };

/** The English catalogue, widened for lookup by arbitrary key. */
const EN: Strings = en;

/** Translate `key` for `lang`, falling back to English, then to the key itself.
 *  A missing translation therefore degrades to readable English rather than to
 *  a blank or a raw key in front of a resident. */
export function translate(lang: string, key: string): string {
  const catalogue = CATALOGUES[lang];
  if (catalogue && catalogue[key] !== undefined) return catalogue[key];
  if (EN[key] !== undefined) return EN[key];
  if (import.meta.env.DEV) console.warn(`[i18n] missing string: ${key}`);
  return key;
}

/** Languages with a catalogue present. The switcher is driven by what the
 *  *backend* can serve, not this — a language the server cannot render a
 *  conversation in must not be offered just because chrome strings exist. */
export const UI_LANGUAGES = Object.keys(CATALOGUES);

type TranslateFn = (key: string) => string;

const LangContext = createContext<{ lang: string; t: TranslateFn }>({
  lang: "en",
  t: (key: string) => translate("en", key),
});

export function LanguageProvider({ lang, children }: { lang: string; children: ReactNode }) {
  const value = useMemo(
    () => ({ lang, t: (key: string) => translate(lang, key) }),
    [lang],
  );
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

/** `const { t, lang } = useI18n()`. Defaults to English outside a provider, so
 *  components stay renderable in isolation (and existing tests keep passing). */
export function useI18n() {
  const ctx = useContext(LangContext);
  const t = useCallback(ctx.t, [ctx]);
  return { lang: ctx.lang, t };
}
